import { Skeleton } from '@/components/ui/skeleton';
import { PublicPropertiesPageSkeleton } from '@/components/loading/public/properties-page-skeletons';
import { AdminSidebarSkeleton } from '@/components/admin/AdminSidebarSkeleton';
import { AdminDashboardHeaderSkeleton } from '@/Dashboard/Layout/AdminDashboardHeaderSkeleton';

function PublicNavbarSkeleton() {
  return (
    <div className="sticky top-0 z-20 border-b border-gray-100 bg-white">
      <div className="mx-auto flex h-[76px] w-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Skeleton className="hidden h-8 w-24 rounded-md bg-gray-200 md:block" />
        <div className="flex flex-1 justify-center md:justify-start">
          <Skeleton className="h-12 w-full max-w-[18rem] rounded-full bg-gray-200 shadow-sm sm:max-w-[24rem]" />
        </div>
        <div className="hidden items-center gap-3 md:flex">
          <Skeleton className="h-5 w-20 rounded-full bg-gray-200" />
          <Skeleton className="h-8 w-8 rounded-full bg-gray-200" />
          <Skeleton className="h-10 w-16 rounded-full bg-gray-200" />
        </div>
        <Skeleton className="h-10 w-10 rounded-full bg-gray-200 md:hidden" />
      </div>
    </div>
  );
}

function PublicFooterSkeleton() {
  return (
    <div className="border-t border-slate-200 bg-white px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
        <Skeleton className="h-24 rounded-[1.5rem] bg-slate-100" />
        <Skeleton className="h-24 rounded-[1.5rem] bg-slate-50" />
        <Skeleton className="h-24 rounded-[1.5rem] bg-slate-100" />
      </div>
    </div>
  );
}

function HeroPanelSkeleton() {
  return (
    <div className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
      <Skeleton className="h-8 w-40 rounded-full bg-gray-200" />
      <Skeleton className="mt-6 h-6 w-56 rounded-full bg-gray-200" />
      <Skeleton className="mt-4 h-14 w-4/5 rounded-[1.25rem] bg-gray-200" />
      <Skeleton className="mt-4 h-5 w-3/5 rounded-full bg-gray-200" />
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        <Skeleton className="h-24 rounded-[1.4rem] bg-gray-100" />
        <Skeleton className="h-24 rounded-[1.4rem] bg-gray-100" />
      </div>
    </div>
  );
}

export function HomeRouteSkeleton() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <PublicNavbarSkeleton />
      <main className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <HeroPanelSkeleton />
        <div className="grid gap-6 lg:grid-cols-[1.35fr_0.95fr]">
          <Skeleton className="h-[420px] rounded-[2rem] bg-slate-100" />
          <div className="grid gap-6">
            <Skeleton className="h-[198px] rounded-[1.8rem] bg-slate-50" />
            <Skeleton className="h-[198px] rounded-[1.8rem] bg-slate-100" />
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <Skeleton className="h-52 rounded-[1.7rem] bg-slate-100" />
          <Skeleton className="h-52 rounded-[1.7rem] bg-slate-50" />
          <Skeleton className="h-52 rounded-[1.7rem] bg-slate-100" />
        </div>
      </main>
      <PublicFooterSkeleton />
    </div>
  );
}

export function OwnerLandingRouteSkeleton() {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <header className="fixed left-0 right-0 top-0 z-20 h-16 border-b border-gray-100 bg-white">
        <div className="mx-auto flex h-full w-full max-w-7xl items-center justify-between px-4 md:px-6">
          <Skeleton className="h-10 w-10 rounded-full bg-gray-200" />
          <Skeleton className="h-3 w-24 rounded-full bg-gray-200" />
          <div className="w-10" />
        </div>
      </header>

      <main className="pt-16">
        <section className="flex min-h-[calc(100vh-4rem)] items-center">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-6 py-12 md:px-12 lg:grid-cols-2 lg:gap-8 lg:px-24 lg:py-0">
            <div className="order-1 flex flex-col justify-center">
              <Skeleton className="h-8 w-52 rounded-md bg-gray-200 sm:h-10 sm:w-64" />
              <Skeleton className="mt-4 h-8 w-full max-w-xl rounded-md bg-gray-200 sm:h-10" />
              <Skeleton className="mt-3 h-8 w-5/6 max-w-lg rounded-md bg-gray-200 sm:h-10" />
              <Skeleton className="mt-8 h-5 w-full max-w-lg rounded-md bg-gray-200" />
              <Skeleton className="mt-3 h-5 w-4/5 max-w-md rounded-md bg-gray-200" />
            </div>

            <div className="order-2 flex items-center justify-center lg:justify-end">
              <Skeleton className="h-[300px] w-full rounded-[2rem] bg-gray-200 sm:h-[400px] lg:h-[500px]" />
            </div>
          </div>
        </section>

        <section className="px-6 pb-8 md:px-12 lg:px-24">
          <Skeleton className="h-44 w-full rounded-[2rem] bg-gray-100" />
        </section>

        <section className="px-6 pb-16 md:px-12 lg:px-24">
          <div className="grid gap-6 md:grid-cols-3">
            <Skeleton className="h-48 rounded-[1.75rem] bg-gray-100" />
            <Skeleton className="h-48 rounded-[1.75rem] bg-gray-100" />
            <Skeleton className="h-48 rounded-[1.75rem] bg-gray-100" />
          </div>
        </section>
      </main>
    </div>
  );
}

export function PublicPropertiesRouteSkeleton() {
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  return <PublicPropertiesPageSkeleton viewport={isMobile ? 'mobile-map' : 'desktop'} />;
}

export function PublicPropertyDetailRouteSkeleton() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <PublicNavbarSkeleton />
      <main className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 pb-20 pt-4 sm:px-6 lg:px-8">
        <Skeleton className="h-[420px] w-full rounded-[2rem] bg-slate-100" />
        <Skeleton className="h-5 w-56 rounded-full bg-slate-100" />
        <Skeleton className="h-10 w-4/5 rounded-[1rem] bg-slate-200" />
        <Skeleton className="h-4 w-2/3 rounded-full bg-slate-100" />
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-6">
            <Skeleton className="h-32 rounded-[1.5rem] bg-slate-100" />
            <Skeleton className="h-28 rounded-[1.5rem] bg-slate-50" />
            <Skeleton className="h-72 rounded-[1.75rem] bg-slate-100" />
            <Skeleton className="h-72 rounded-[1.75rem] bg-slate-50" />
          </div>
          <div className="hidden xl:block">
            <Skeleton className="h-[560px] rounded-[1.75rem] bg-slate-100" />
          </div>
        </div>
      </main>
    </div>
  );
}

export function PublicAuthRouteSkeleton() {
  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0 lg:flex">
      <div className="relative h-44 w-full overflow-hidden lg:hidden">
        <Skeleton className="h-full w-full rounded-none bg-slate-100" />
      </div>

      <div className="hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-between lg:p-12">
        <Skeleton className="h-full rounded-[2rem] bg-gray-100" />
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-12 lg:px-12">
        <div className="w-full max-w-md space-y-6">
          <Skeleton className="h-4 w-24 rounded-full bg-gray-200" />
          <div className="space-y-4">
            <Skeleton className="h-16 w-16 rounded-3xl bg-gray-200" />
            <Skeleton className="h-8 w-3/4 rounded-full bg-gray-200" />
            <Skeleton className="h-4 w-5/6 rounded-full bg-gray-200" />
          </div>
          <div className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.25)]">
            <Skeleton className="h-12 rounded-full bg-gray-200" />
            <Skeleton className="h-12 rounded-full bg-gray-200" />
            <Skeleton className="h-12 rounded-full bg-gray-200" />
            <Skeleton className="h-14 rounded-full bg-gray-200" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function PublicContentRouteSkeleton() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <PublicNavbarSkeleton />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
        <Skeleton className="h-10 w-48 rounded-full bg-slate-200" />
        <Skeleton className="h-24 rounded-[1.8rem] bg-slate-100" />
        <Skeleton className="h-64 rounded-[1.8rem] bg-slate-50" />
        <Skeleton className="h-52 rounded-[1.8rem] bg-slate-100" />
      </main>
      <PublicFooterSkeleton />
    </div>
  );
}

export function DashboardTabSkeleton() {
  return (
    <div className="w-full space-y-6 animate-in fade-in duration-300">
      <div className="skeleton-shimmer h-10 w-48 rounded-full bg-slate-200/60 dark:bg-zinc-800/60" />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div className="skeleton-shimmer h-40 rounded-[1.8rem] bg-slate-100/60 dark:bg-zinc-800/40" />
        <div className="skeleton-shimmer h-40 rounded-[1.8rem] bg-slate-100/60 dark:bg-zinc-800/40" />
        <div className="skeleton-shimmer h-40 rounded-[1.8rem] bg-slate-100/60 dark:bg-zinc-800/40" />
      </div>
      <div className="skeleton-shimmer h-[400px] rounded-[2rem] bg-slate-50/60 dark:bg-zinc-800/20" />
    </div>
  );
}

export function PrivateRouteSkeleton() {
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const skeletonSidebarOffset = isMobile ? 0 : 256;

  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-[hsl(var(--background-app))] md:flex-row">
      {!isMobile && (
        <AdminSidebarSkeleton expanded={true} showDigitalCard={true} />
      )}
      <main
        className="flex-1 overflow-hidden"
        style={!isMobile ? { marginLeft: skeletonSidebarOffset } : {}}
      >
        <AdminDashboardHeaderSkeleton isMobile={isMobile} />
        <div className="p-6">
          <DashboardTabSkeleton />
        </div>
      </main>
    </div>
  );
}